import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BookOpen, Search, Scale, Users, ChevronRight, Shield, MessageSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WelcomeBeta() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [roleInfo, setRoleInfo] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/team/my-role`, {
      headers: { 'Authorization': `Bearer ${token}` },
      credentials: 'include',
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setRoleInfo(data); })
      .catch(() => {});
  }, [token]);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-stone-200">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-900/10 via-transparent to-transparent" />
        <div className="max-w-4xl mx-auto px-6 py-20 text-center relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-900/20 border border-amber-700/30 text-amber-500 text-xs tracking-widest uppercase mb-8">
            <Shield className="w-3.5 h-3.5" />
            Maiden Voyage — Beta Testing Program
          </div>

          <h1 className="text-4xl sm:text-5xl font-bold text-stone-100 leading-tight mb-6"
            style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>
            The GIT Protocol
          </h1>

          <p className="text-lg text-stone-400 max-w-2xl mx-auto leading-relaxed mb-4">
            <strong className="text-amber-400">Grammar. Interpretation. Theology.</strong>
          </p>

          <p className="text-base text-stone-400 max-w-2xl mx-auto leading-relaxed">
            A systematic method for studying the Word of God that removes the fog of human tradition, doctrinal bias, and presumption — so that Scripture itself becomes the final authority on what it says.
          </p>
        </div>
      </section>

      {/* What is GIT */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="p-6 rounded-xl border border-stone-800 bg-stone-900/30">
            <div className="w-10 h-10 rounded-lg bg-amber-900/30 flex items-center justify-center mb-4">
              <BookOpen className="w-5 h-5 text-amber-500" />
            </div>
            <h3 className="text-base font-bold text-stone-200 mb-2">G — Grammar</h3>
            <p className="text-sm text-stone-400 leading-relaxed">
              What does the text actually say? Before we interpret, we read. Word by word, in context, as the original audience would have understood it.
            </p>
          </div>

          <div className="p-6 rounded-xl border border-stone-800 bg-stone-900/30">
            <div className="w-10 h-10 rounded-lg bg-blue-900/30 flex items-center justify-center mb-4">
              <Search className="w-5 h-5 text-blue-400" />
            </div>
            <h3 className="text-base font-bold text-stone-200 mb-2">I — Interpretation</h3>
            <p className="text-sm text-stone-400 leading-relaxed">
              What does it mean? We compare scripture with scripture. We identify WHO is speaking, WHAT is being said, and WHEN it applies — letting the Bible interpret itself.
            </p>
          </div>

          <div className="p-6 rounded-xl border border-stone-800 bg-stone-900/30">
            <div className="w-10 h-10 rounded-lg bg-purple-900/30 flex items-center justify-center mb-4">
              <Scale className="w-5 h-5 text-purple-400" />
            </div>
            <h3 className="text-base font-bold text-stone-200 mb-2">T — Theology</h3>
            <p className="text-sm text-stone-400 leading-relaxed">
              What does it teach? Only after grammar and interpretation do we form doctrine. The Word of God is the standard — not our own understanding, traditions, biases, or presumptions.
            </p>
          </div>
        </div>

        {/* The Purpose */}
        <div className="rounded-xl border border-amber-800/20 bg-amber-900/5 p-8 mb-16">
          <h2 className="text-xl font-bold text-amber-400 mb-4"
            style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>
            The Purpose of This Ministry
          </h2>
          <div className="space-y-4 text-sm text-stone-400 leading-relaxed">
            <p>
              Trump of the Lord Ministries exists for one reason: <strong className="text-stone-200">to rightly understand the Word of God.</strong>
            </p>
            <p>
              For too long, the Church has inherited doctrines built on tradition rather than text. Phrases get repeated from pulpit to pulpit until they sound like Scripture — but when you go back to the grammar, back to the original language, back to the context — what you find is often very different from what you were taught.
            </p>
            <p>
              The GIT protocol is a tool for honest investigation. It asks: <em className="text-stone-300">"What if we put aside what we've been told and just read what it says?"</em> Not to tear down faith, but to build it on the unshakeable foundation of what God actually said.
            </p>
            <p>
              <strong className="text-stone-200">The King's Return</strong> is our first curriculum — a deep investigation into what Scripture actually teaches about the return of Christ, the gathering of the saints, and the events surrounding the last day. Every lesson uses the GIT protocol to examine the text, compare the arguments, and let the Word speak for itself.
            </p>
          </div>
        </div>

        {/* Beta Tester Role */}
        <div className="rounded-xl border border-cyan-800/20 bg-cyan-900/5 p-8 mb-16">
          <div className="flex items-center gap-3 mb-4">
            <MessageSquare className="w-5 h-5 text-cyan-400" />
            <h2 className="text-xl font-bold text-cyan-400"
              style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>
              Your Role as a Beta Tester
            </h2>
          </div>
          <div className="space-y-4 text-sm text-stone-400 leading-relaxed">
            <p>
              You've been invited to be among the <strong className="text-stone-200">first people</strong> to go through this curriculum. Your job is simple but critical:
            </p>
            <ol className="list-decimal ml-6 space-y-3">
              <li>
                <strong className="text-stone-300">Go through each lesson</strong> — Take your time. Read the GIT panels. Consider the three voices. Think through the arguments.
              </li>
              <li>
                <strong className="text-stone-300">Answer the feedback questions</strong> — At the bottom of every lesson, you'll see two questions:
                <ul className="list-disc ml-4 mt-1 text-stone-500">
                  <li><em>"What does this lesson cause you to think?"</em> — One word or a short sentence. Your gut reaction.</li>
                  <li><em>"What could be resaid to improve the thought logic process?"</em> — Where did the lesson lose you? What needs to be clearer?</li>
                </ul>
              </li>
              <li>
                <strong className="text-stone-300">Rate each lesson</strong> — A simple 1-5 star rating tells us how effective it is.
              </li>
            </ol>
            <p className="pt-2">
              Your feedback directly shapes this ministry. Once you complete the curriculum, you may be invited to take on a dedicated editorial role — contributing your gifts to the content itself.
            </p>
          </div>
        </div>

        {/* Pending Role Banner */}
        {roleInfo?.pending_editorial_role && (
          <div className="rounded-xl border border-emerald-800/20 bg-emerald-900/5 p-6 mb-16" data-testid="pending-role-banner">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-emerald-400" />
              <div>
                <p className="text-sm font-bold text-emerald-400">Your Future Role</p>
                <p className="text-sm text-stone-400">
                  After completing the curriculum, you'll be promoted to{' '}
                  <strong className="text-stone-200">{roleInfo.pending_editorial_role_label}</strong>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center">
          <button
            data-testid="begin-curriculum-btn"
            onClick={() => navigate('/explore')}
            className="inline-flex items-center gap-2 px-8 py-3 rounded-lg bg-amber-600 hover:bg-amber-500 text-white font-bold text-sm tracking-wide transition-colors"
          >
            Begin The King's Return <ChevronRight className="w-4 h-4" />
          </button>
          <p className="text-xs text-stone-600 mt-3">
            15 lessons published and ready for your review
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
